import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Remounts the subtree when this changes — used to recover on navigation. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * A render error anywhere below this boundary would otherwise unmount the whole
 * app and leave a blank page — the worst possible failure mode for a trading UI,
 * because it looks identical to a network outage. This keeps the chrome intact,
 * says what happened, and offers a way out.
 *
 * Deliberately styled with plain tokens and no imports from `@/components/ui`:
 * if a primitive is what broke, the fallback must not depend on it.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // No telemetry sink yet; the console is the only place this can go.
    console.error('Unhandled render error', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="shell flex min-h-[60vh] flex-col items-center justify-center gap-4 py-16 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-line bg-elevated text-warn">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3.5 21 19.5H3L12 3.5Z M12 10v4 M12 17h.01"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h1 className="font-display text-2xl font-semibold">Something broke on this screen</h1>
        <p className="max-w-md text-sm text-muted">
          The rest of the app is still running. Reloading usually clears it — no funds or
          transactions are affected by a rendering fault.
        </p>
        <pre className="max-w-full overflow-x-auto rounded-lg border border-line bg-surface px-3 py-2 text-left font-mono text-2xs text-subtle">
          {error.message || String(error)}
        </pre>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-fg transition-colors hover:border-line-strong"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.assign('/')}
            className="rounded-md bg-bull-sheen px-4 py-2 text-sm font-semibold text-[rgb(var(--on-bull))]"
          >
            Back to explore
          </button>
        </div>
      </div>
    );
  }
}
